/* Jaguar additions. Awaken owns scrolling, animation, tabs, cart and forms. */
if (!customElements.get('jd1-video-cover')) {
  customElements.define('jd1-video-cover', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('button');
      if (!this.video || !this.button || this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.mobile = matchMedia('(max-width: 767px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.reduced.matches;
      this.visible = false;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.video.autoplay = !this.userPaused;
      this.button.hidden = false;
      this.syncButton = () => {
        const playing = !this.video.paused;
        this.button.setAttribute('aria-pressed', String(playing));
        this.button.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
        this.button.querySelector('[data-play]').hidden = playing;
        this.button.querySelector('[data-pause]').hidden = !playing;
        this.button.hidden = this.hasAttribute('data-hide-mobile-pause') && this.mobile.matches && playing;
      };
      this.tryPlay = () => {
        this.video.autoplay = this.visible && !this.userPaused && !document.hidden;
        if (this.visible && !this.userPaused && !document.hidden) {
          this.video.play().catch(this.syncButton);
        }
      };
      this.chooseSource = () => {
        this.video.autoplay = this.visible && !this.userPaused && !document.hidden;
        const source = this.mobile.matches ? this.dataset.mobile : this.dataset.desktop;
        if (source && this.video.getAttribute('src') !== source) {
          this.video.src = source;
          this.video.load();
        }
        this.tryPlay();
        this.syncButton();
      };
      this.button.addEventListener('click', () => {
        this.userPaused = !this.video.paused;
        this.video.autoplay = !this.userPaused;
        if (this.userPaused) this.video.pause();
        else { this.chooseSource(); this.video.play().catch(() => {}); }
      }, { signal });
      this.video.addEventListener('play', this.syncButton, { signal });
      this.video.addEventListener('pause', this.syncButton, { signal });
      this.video.addEventListener('loadeddata', this.tryPlay, { signal });
      this.video.addEventListener('canplay', this.tryPlay, { signal });
      this.mobile.addEventListener('change', this.chooseSource, { signal });
      this.reduced.addEventListener('change', () => {
        this.userPaused = this.reduced.matches;
        this.video.autoplay = !this.userPaused;
        if (this.userPaused) this.video.pause(); else this.chooseSource();
      }, { signal });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .15;
        this.video.autoplay = this.visible && !this.userPaused && !document.hidden;
        if (this.visible) this.chooseSource(); else this.video.pause();
      }, { threshold: [0, .15] });
      this.observer.observe(this);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.video.pause(); else if (this.visible) this.chooseSource();
      }, { signal });
      window.addEventListener('pageshow', this.tryPlay, { signal });
      window.addEventListener('pagehide', () => this.video.pause(), { signal });
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.observer?.disconnect(); this.video?.pause();
    }
  });
}

/* Mobile documentary previews are lazy, silent and never run behind a modal. */
if (!customElements.get('jd1-mobile-film')) {
  customElements.define('jd1-mobile-film', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.screen = this.querySelector('[data-mobile-film-screen]');
      this.poster = this.querySelector('[data-mobile-film-poster]');
      this.template = this.querySelector('template');
      if (!this.screen || !this.poster || !this.template) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.mobile = matchMedia('(max-width: 991px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.modal = document.querySelector(`xo-modal.jd1-film-modal[xo-name="${CSS.escape(this.dataset.modal)}"]`);
      this.sync = () => {
        const enabled = this.mobile.matches && !this.reduced.matches;
        const modalOpen = this.modal?.hasAttribute('xo-active') && this.modal.getAttribute('xo-active') !== 'false';
        this.hidden = !enabled;
        if (enabled && this.visible && !document.hidden && !modalOpen) {
          if (!this.screen.childElementCount) this.screen.append(this.template.content.cloneNode(true));
          this.poster.hidden = true;
        } else {
          this.stop();
        }
      };
      this.mobile.addEventListener('change', this.sync, { signal });
      this.reduced.addEventListener('change', this.sync, { signal });
      document.addEventListener('visibilitychange', this.sync, { signal });
      window.addEventListener('pageshow', this.sync, { signal });
      window.addEventListener('pagehide', () => this.stop(), { signal });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .15;
        this.sync();
      }, { threshold: [0, .15] });
      this.observer.observe(this);
      if (this.modal) {
        this.modalObserver = new MutationObserver(this.sync);
        this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['xo-active'] });
      }
      this.sync();
    }
    stop() {
      this.screen?.querySelectorAll('video').forEach(video => video.pause());
      this.screen?.replaceChildren();
      if (this.poster) this.poster.hidden = false;
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.observer?.disconnect(); this.modalObserver?.disconnect(); this.stop();
    }
  });
}

/* Editorial cart link delegates to the PDP's own native action so variant,
   quantity, selling plan, inventory errors and cart drawer stay in Awaken. */
document.addEventListener('click', event => {
  const button = event.target.closest('[data-jd1-pdp-add]');
  if (!button) return;
  const purchase = button.closest('.jd1-product-purchase');
  const main = document.querySelector('#jaguar-pdp-product');
  const mainProduct = main?.closest('xo-product') || main?.querySelector('xo-product.jd1-pdp-purchase-live');
  if (!mainProduct || mainProduct.getAttribute('xo-product-id') !== purchase?.getAttribute('xo-product-id')) return;
  const add = main.querySelector('.xo-buy-buttons__add-to-cart-detail xo-cart-add');
  const nativeButton = add?.querySelector('button');
  if (!nativeButton) return; // The existing native variant chooser remains the fallback.
  event.preventDefault();
  event.stopImmediatePropagation();
  const status = purchase.querySelector('[data-jd1-cart-status]');
  const flagged = (element, attribute) => element?.hasAttribute(attribute) && element.getAttribute(attribute) !== 'false';
  if (nativeButton.disabled || flagged(add, 'xo-disabled') || flagged(mainProduct, 'xo-disabled') || flagged(mainProduct, 'xo-unavailable')) {
    if (status) status.textContent = 'This option is unavailable. Please choose another option above.';
    return;
  }
  const api = window.xobuildercomponents;
  if (button.getAttribute('aria-busy') === 'true' || api?.getCartState?.().addIdLoading) return;
  if (status) status.textContent = '';
  if (api?.cartSubscribe && add.getOptions && add.getSectionId) {
    const requestId = `${add.getSectionId()}/${add.getOptions().xoProductId}`;
    let started = false;
    let unsubscribe = () => {};
    button.setAttribute('aria-busy', 'true');
    if (status) status.textContent = 'Adding to cart…';
    const finish = () => { unsubscribe(); button.removeAttribute('aria-busy'); };
    unsubscribe = api.cartSubscribe(state => {
      if (state.addIdLoading === requestId) started = true;
      if (!started || state.addIdLoading) return;
      finish();
      if (status) status.textContent = state.addErrorMessage || (state.isAdded ? 'Added to cart.' : 'Please try again.');
    });
    queueMicrotask(() => {
      if (!started) {
        finish();
        if (status) status.textContent = '';
      }
    });
  }
  nativeButton.click();
}, true);

/* Finale P1: Awaken opens/closes the modal; mount and unload its player on demand. */
if (!customElements.get('jd1-film-player')) {
  customElements.define('jd1-film-player', class extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;
      this.modal = this.closest('xo-modal');
      this.screen = this.querySelector('[data-film-screen]');
      this.template = this.querySelector('template');
      if (!this.modal || !this.screen || !this.template) return;
      this.sync = () => {
        const open = this.modal.hasAttribute('xo-active') && this.modal.getAttribute('xo-active') !== 'false';
        if (open && !this.screen.childElementCount) {
          this.screen.append(this.template.content.cloneNode(true));
          this.screen.querySelector('video')?.play()?.catch(() => {});
        } else if (!open) {
          this.stop();
        }
      };
      this.observer = new MutationObserver(this.sync);
      this.observer.observe(this.modal, { attributes: true, attributeFilter: ['xo-active'] });
      this.sync();
    }
    stop() {
      this.screen?.querySelectorAll('video').forEach(video => video.pause());
      this.screen?.replaceChildren();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      this.stop();
    }
  });
}
